import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import store from '~/shared/store';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import {getLocalizationService} from '~/shared/services/localisationService';
import AddCreditCard from '~/shared/components/AddCreditCard/AddCreditCard';
import {MoneycardOtlRuleManagement} from '~/shared/store/models';
import {trackEvent} from '~/shared/services/analytics';
import frustratedImg from '~/assets/images/frustratedImg.png';
import {getNonExpiredModifiedCreditCards} from '~/shared/utils/payments/payments';

import changeConfirmSrc from './assets/changeConfirm.png';

export const linkCreditCardHandler = (
  currentMoneycard: MoneyCard,
  otlDataFromProps: MoneycardOtlRuleManagement | null,
  fetchOtl: () => Promise<MoneycardOtlRuleManagement | null>,
  refetchPayments: () => Promise<void>,
) => {
  const userCreditCards = getNonExpiredModifiedCreditCards(otlDataFromProps?.userCreditcards || []);
  trackEvent('linkCC2MC_click');
  const {t} = getLocalizationService();

  const getFailureModalObject = (headerKey: string) => ({
    imgSrc: frustratedImg,
    imgAlt: t('failure_picture'),
    header: t(headerKey),
    subHeader: t('try_again_later_2'),
    primaryButton: {
      label: t('close_2'),
      onClick: () => store.dispatch(setCurrentModal(null)),
    },
  });
  const getFailureModal = (headerKey: string) => () => {
    store.dispatch(setCurrentModal('moneycardFeedbackModal', getFailureModalObject(headerKey)));
  };
  const openOtlAction = () => {
    store.dispatch(setCurrentModal('otlModal', {
      showBackArrow: false,
      showUnlinkButton: true,
      onSuccess: () => {
        fetchOtl();
        store.dispatch(setCurrentModal('moneycardFeedbackModal', {
          imgSrc: changeConfirmSrc,
          imgAlt: t('success'),
          header: t('great_your_changes_were_saved'),
          primaryButton: {
            label: t('close_2'),
            onClick: () => store.dispatch(setCurrentModal(null)),
          },
        }));
      },
      onFailure: () =>
        store.dispatch(setCurrentModal('moneycardFeedbackModal', getFailureModalObject('the_changes_were_not_saved'))),
    }));
  };
  const getLinkCreditcardModal = ({addCreditCardAction: addCCAction, hasSuccessfullyAddedCreditCard, otlData}: {
    addCreditCardAction: () => void;
    hasSuccessfullyAddedCreditCard?: boolean;
    otlData?: MoneycardOtlRuleManagement | null;
  }) => () => {
    store.dispatch(setCurrentModal('linkMoneycardModal', {
      currentMoneycard,
      otlData: otlData || otlDataFromProps,
      addCreditCardAction: addCCAction,
      hasSuccessfullyAddedCreditCard,
      onSuccess: fetchOtl,
      onLinkFailed: getFailureModal('we_couldnt_link_the_credit_card_to_your_10bis'),
      openOtlAction,
    }));
  };

  const addCreditCardAction = () =>
    store.dispatch(setCurrentModal('addPaymentMethodModal', {
      headerTitle: 'add_a_credit_card',
      AddCreditCardComponent: AddCreditCard,
      onSuccess: async () => {
        await refetchPayments();
        const otlData = await fetchOtl();
        getLinkCreditcardModal({
          addCreditCardAction,
          hasSuccessfullyAddedCreditCard: true,
          otlData,
        })();
      },
      onFailure: getFailureModal('credit_card_addition_failed'),
    }));

  const linkMCModal = getLinkCreditcardModal({addCreditCardAction});

  if (otlDataFromProps && otlDataFromProps.otlRule.ruleActive && otlDataFromProps.otlRule.creditcardId) {
    const isCreditCardExist = userCreditCards.some(ucc => ucc.creditcardId === otlDataFromProps.otlRule.creditcardId);
    if (!isCreditCardExist) {
      if (!userCreditCards.length) {
        addCreditCardAction();
      }
      if (userCreditCards.length) {
        linkMCModal();
      }
      return;
    }
    openOtlAction();
    return;
  }

  if (!userCreditCards || !userCreditCards.length) {
    addCreditCardAction();
    return;
  }
  linkMCModal();
};

export const manageBillingPermissions = (currentMoneycard: MoneyCard) => {
  trackEvent('billingPermissions_click');
  store.dispatch(setCurrentModal('billingPermissions', {moneycardId: currentMoneycard.id}));
};

export const moneycardLostOrStolen = (currentMoneycard: MoneyCard, refetchPayments: () => Promise<void>) => {
  trackEvent('mcLost_click');
  store.dispatch(setCurrentModal('reIssueOrReplaceMoneycard', {
    moneycardId: currentMoneycard.id,
    isReIssue: false,
    onSuccess: refetchPayments,
  }));
};

export const moneycardWornOrNotWorking = (currentMoneycard: MoneyCard, refetchPayments: () => Promise<void>) => {
  trackEvent('mcWorn_click');
  store.dispatch(setCurrentModal('reIssueOrReplaceMoneycard', {
    moneycardId: currentMoneycard.id,
    isReIssue: true,
    onSuccess: refetchPayments,
  }));
};
